import React, { useState } from "react";
import api, { formatDuration, formatDurationDays, isMultiDay, formatTL, formatDateTime } from "@/lib/api";
import { useLiveQuery, TAGS } from "@/lib/liveSync";
import { waiveAsGuest } from "@/pages/admin/Live";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plate } from "@/components/Plate";
import PayMethodDialog from "@/components/PayMethodDialog";
import { BusinessTagSelect, BusinessBadge, BusinessQuotaStrip, BusinessQuickTag } from "@/components/BusinessTag";
import {
  Warning, CarProfile, DoorOpen, ImageSquare, ArrowRight, CheckCircle, Gift,
} from "@phosphor-icons/react";

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const diffS = Math.floor((Date.now() - t) / 1000);
  if (diffS < 60) return `${diffS}sn önce`;
  if (diffS < 3600) return `${Math.floor(diffS / 60)}dk önce`;
  const h = Math.floor(diffS / 3600);
  return `${h}sa önce`;
}

export default function OperatorHome() {
  const [payFor, setPayFor] = useState(null);

  const { data: summary } = useLiveQuery(
    [TAGS.SESSIONS, TAGS.UNPAID],
    async () => (await api.get("/reports/summary")).data,
    { intervalMs: 5000 },
  );
  const { data: lots } = useLiveQuery(
    TAGS.PARKING_LOTS,
    async () => (await api.get("/parking-lots")).data,
    { intervalMs: 8000 },
  );
  const { data: active } = useLiveQuery(
    TAGS.SESSIONS,
    async () => (await api.get("/sessions/active")).data,
    { intervalMs: 5000 },
  );
  const { data: justExited } = useLiveQuery(
    [TAGS.SESSIONS, TAGS.UNPAID],
    async () => (await api.get("/sessions/just-exited?minutes=30&limit=10")).data,
    { intervalMs: 3000 }, // very fresh: 3s polling for real-time exit awareness
  );

  const activeList = active || [];
  const exitedList = justExited || [];
  const unpaidExits = exitedList.filter((s) => s.payment_status === "bekliyor");

  return (
    <div className="space-y-5 animate-fade-in">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Operatör</div>
        <h1 className="text-3xl font-bold tracking-tight">Ana Sayfa</h1>
      </div>

      <BusinessQuotaStrip />

      {/* -------- Just Exited - hero panel for collection -------- */}
      {unpaidExits.length > 0 && (
        <Card className="rounded-sm border-2 border-primary bg-primary/5" data-testid="op-just-exited-panel">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="pulse-dot bg-primary" />
                <h3 className="font-bold uppercase tracking-widest text-sm">
                  Az Önce Çıktı — Tahsilat Bekliyor ({unpaidExits.length})
                </h3>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Her 3sn güncellenir
              </div>
            </div>
            <div className="space-y-3">
              {unpaidExits.map((s) => (
                <ExitedCard key={s.id} s={s} onPay={() => setPayFor(s)} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Active vehicles count */}
      <div className="grid grid-cols-2 gap-3">
        <Card className="rounded-sm border-border">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Aktif Araç</div>
                <div className="text-4xl font-bold text-primary mt-2 font-mono" data-testid="op-active-total">
                  {summary?.active_total ?? 0}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {summary?.active_ust_kat ?? 0} üst / {summary?.active_alt_kat ?? 0} alt
                </div>
              </div>
              <CarProfile size={22} className="text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-border">
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Bugün Giriş</div>
                <div className="text-4xl font-bold mt-2 font-mono" data-testid="op-today-entries">
                  {summary?.today_entries ?? 0}
                </div>
                <div className="text-xs text-muted-foreground mt-1">{summary?.today_sessions ?? 0} çıkış</div>
              </div>
              <DoorOpen size={22} className="text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      </div>

      {summary?.unpaid_count > 0 && (
        <Card className="rounded-sm border-destructive/40 bg-destructive/5">
          <CardContent className="p-5 flex items-center gap-3">
            <Warning size={26} className="text-destructive" />
            <div className="flex-1">
              <div className="font-semibold" data-testid="op-unpaid-count">
                {summary.unpaid_count} ödenmemiş oturum
              </div>
              <div className="text-sm text-muted-foreground">Detay için Ödenmemiş sekmesine gidin</div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-3">
        {(lots || []).map((l) => {
          const pct = Math.min(100, Math.round((l.current_occupancy / (l.capacity || 100)) * 100));
          return (
            <Card key={l.id} className="rounded-sm border-border">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold">{l.name}</div>
                  <div className="text-sm font-mono">{l.current_occupancy}/{l.capacity}</div>
                </div>
                <Progress value={pct} className="h-2 rounded-sm" />
                <div className="text-xs text-muted-foreground mt-1">%{pct} dolu</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Active parking - shows running fee prominently */}
      <div>
        <h3 className="text-sm font-semibold uppercase tracking-widest mb-3 flex items-center gap-2">
          <span className="pulse-dot" /> Aktif Araçlar · Anlık Ücret ({activeList.length})
        </h3>
        <Card className="rounded-sm border-border">
          <CardContent className="p-0 divide-y divide-border">
            {activeList.length === 0 && (
              <div className="p-6 text-center text-muted-foreground text-sm">Aktif araç yok</div>
            )}
            {activeList.map((s) => (
              <div key={s.id} className="p-3 flex items-center justify-between gap-2" data-testid={`op-active-${s.plate}`}>
                <div className="flex items-center gap-3 flex-1 min-w-0 flex-wrap">
                  <Plate country={s.country_code}>{s.plate}</Plate>
                  <div className="text-[11px] text-muted-foreground uppercase tracking-widest">
                    {s.parking_lot_key === "ust_kat" ? "ÜST" : "ALT"}
                  </div>
                  <BusinessBadge session={s} />
                  <BusinessTagSelect session={s} compact />
                </div>
                <div className="text-right">
                  <div className="text-xs text-muted-foreground flex items-center justify-end gap-1.5">
                    {isMultiDay(s.current_duration_minutes) && (
                      <Badge data-testid={`op-multiday-${s.plate}`}
                             className="rounded-sm bg-red-600 hover:bg-red-600 text-white text-[9px] uppercase tracking-widest border-transparent">
                        {formatDurationDays(s.current_duration_minutes)}
                      </Badge>
                    )}
                    {formatDuration(s.current_duration_minutes)}
                  </div>
                  <div className="font-mono font-bold text-primary text-lg leading-tight" data-testid={`op-active-fee-${s.plate}`}>
                    {s.subscriber_free ? (
                      <span className="text-emerald-600 text-sm">MUAF</span>
                    ) : (
                      formatTL(s.running_fee)
                    )}
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <PayMethodDialog
        session={payFor}
        open={!!payFor}
        onOpenChange={(o) => !o && setPayFor(null)}
        onDone={() => setPayFor(null)}
      />
    </div>
  );
}

function ExitedCard({ s, onPay }) {
  const isSubscriber = !!s.subscription_live_detected || !!s.is_subscriber
                        || !!s.subscription_used || s.payment_status === "muaf";
  return (
    <div className={`grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-3 items-center bg-white rounded-sm border p-3 ${isSubscriber ? "border-green-500 border-2 bg-green-50/40" : "border-primary/30"}`} data-testid={`op-exited-${s.plate}`}>
      {/* Photos */}
      <div className="flex gap-2">
        <PhotoTile src={s.entry_image_url} label="Giriş" />
        <ArrowRight size={16} className="text-muted-foreground self-center" />
        <PhotoTile src={s.exit_image_url} label="Çıkış" />
      </div>
      {/* Info */}
      <div className="min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <Plate country={s.country_code}>{s.plate}</Plate>
          <BusinessBadge session={s} />
          {isSubscriber && (
            <Badge
              data-testid={`op-exited-muaf-${s.plate}`}
              className="rounded-sm bg-green-600 hover:bg-green-600 text-white uppercase tracking-widest text-[9px] font-semibold gap-1 border-transparent"
            >
              <CheckCircle size={10} weight="fill" /> Muaf (Abone)
            </Badge>
          )}
          <Badge className="rounded-sm text-[9px] uppercase tracking-widest bg-primary/10 text-primary border-transparent">
            {s.parking_lot_key === "ust_kat" ? "Üst Kat" : "Alt Kat"}
          </Badge>
          <span className="text-[10px] text-muted-foreground uppercase tracking-widest">
            {timeAgo(s.exit_time)}
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground font-mono">
          {formatDateTime(s.entry_time)} → {formatDateTime(s.exit_time)}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Süre: <span className="font-mono">{formatDuration(s.duration_minutes)}</span>
        </div>
        <BusinessQuickTag session={s} className="mt-2" />
      </div>
      {/* Fee + Action */}
      <div className="text-right md:min-w-[140px]">
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {isSubscriber ? "Muaf" : "Alınacak"}
        </div>
        <div className={`text-3xl font-bold font-mono leading-none ${isSubscriber ? "text-green-700 line-through opacity-70" : "text-primary"}`} data-testid={`op-exited-fee-${s.plate}`}>
          {formatTL(s.fee)}
        </div>
        <Button
          size="sm"
          onClick={onPay}
          data-testid={`op-pay-${s.plate}`}
          className={`mt-2 rounded-sm uppercase tracking-widest text-[11px] font-bold w-full ${isSubscriber ? "bg-green-600 hover:bg-green-700 text-white" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
        >
          <CheckCircle size={12} weight="fill" /> {isSubscriber ? "Muaf İşaretle" : "Ödendi"}
        </Button>
        {!isSubscriber && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => waiveAsGuest(s)}
            data-testid={`op-guest-${s.plate}`}
            className="mt-1.5 rounded-sm uppercase tracking-widest text-[11px] font-bold w-full"
          >
            <Gift size={12} /> Misafir Olarak Ekle
          </Button>
        )}
      </div>
    </div>
  );
}

function PhotoTile({ src, label }) {
  const [failed, setFailed] = React.useState(false);
  const showImg = src && !failed;
  return (
    <div className="relative w-16 h-16 rounded-sm overflow-hidden bg-muted border border-border shrink-0" data-testid={`op-photo-${label}`}>
      {showImg ? (
        <img
          src={src}
          alt={label}
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground gap-0.5">
          <ImageSquare size={22} />
          <span className="text-[8px] uppercase tracking-widest opacity-60">Yok</span>
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 bg-black/70 text-white text-[9px] uppercase tracking-widest text-center py-0.5">
        {label}
      </div>
    </div>
  );
}
